import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ArrowRight, Check, UtensilsCrossed } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { NoAccess } from "@/components/NoAccess";
import { BundleNote } from "@/components/kit/BundleNote";
import { EmptyState } from "@/components/kit/EmptyState";
import { MealTile } from "@/components/kit/MealTile";
import { RecipeModal } from "@/components/kit/RecipeModal";
import { SLOT_LABELS, api, routes, sortSlots } from "@/lib/ppcp";
import { DaySkeleton } from "@/components/kit/Skeleton";
import { track } from "@/lib/analytics";
import { useProgress } from "@/lib/progress";
import { cn } from "@/lib/utils";
import { KIT, PAGES } from "@/constants/testIds";

export default function DayPage() {
  const { slug, dayNumber } = useParams();
  const navigate = useNavigate();
  const n = Number(dayNumber);
  const { summary, setMeal } = useProgress();

  const [payload, setPayload] = useState(null);
  const [denied, setDenied] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const reported = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setPayload(null);
    api
      .get(`/programs/${slug}/days/${n}`)
      .then((res) => {
        setPayload(res.data);
        // One event per day opened, however many times the effect runs.
        const id = `${slug}/${n}`;
        if (reported.current !== id) {
          reported.current = id;
          track("day_viewed", { day_number: n, plan_slug: slug, plan_name: res.data?.program_title });
        }
      })
      .catch((err) => (err.response?.status === 403 ? setDenied(true) : setPayload(false)));
  }, [slug, n]);

  const day = payload?.day;
  const meals = useMemo(() => sortSlots(day?.meals ?? []), [day]);

  if (denied) return <NoAccess title={`Day ${n}`} />;

  // The day's meals are still arriving: show their shape rather than an empty hero.
  if (payload === null)
    return (
      <AppShell
        title={`Day ${n}`}
        slug={slug}
        todayHref={routes.day(slug, n)}
        size="reading"
        crumbs={[
          { label: "My Plans", to: routes.library },
          { label: "Program", to: routes.program(slug) },
          { label: "Day Index", to: routes.days(slug) },
          { label: `Day ${n}` },
        ]}
      >
        <DaySkeleton />
      </AppShell>
    );

  const isBundle = payload?.tier === "bundle";
  // Basic tier: the protein prompt appears once per day, on the first meal that has guidance.
  const firstProteinSlot = meals.find((m) => m.protein_locked)?.slot;
  // Progress is keyed on this day number, so a day sharing a template never shares its state.
  const eaten = summary?.meals?.[n] ?? [];
  const requiredSlots = meals.filter((m) => m.required !== false).map((m) => m.slot);
  const allRequiredDone = !!requiredSlots.length && requiredSlots.every((s) => eaten.includes(s));
  // The plan-ahead sentence is authored inside the day note; it reads as its own card.
  const noteParts = (day?.note ?? "").split(/\s*Plan ahead:\s*/);
  const dayNote = noteParts[0] || null;
  const planAhead = noteParts.length > 1 ? noteParts.slice(1).join(" ").trim() : null;
  const total = payload?.duration_days ?? summary?.total_days ?? 0;
  const isLastDay = n >= total;
  const daysComplete = summary?.days_complete ?? 0;
  // A week just closed: pause on the milestone before opening the next week.
  const closesAWeek = n % 7 === 0 && !isLastDay;
  const onwardHref = isLastDay
    ? routes.completion(slug)
    : closesAWeek
      ? routes.week(slug, n / 7)
      : routes.day(slug, n + 1);

  /** Auto-save: ticking a meal saves itself, optimistically. No Save button, ever. */
  const toggle = (meal) => setMeal(n, meal.slot, !eaten.includes(meal.slot), requiredSlots);

  const openRecipe = async (meal) => {
    setLoadingRecipe(true);
    track("recipe_opened", {
      recipe_name: meal.title,
      meal_type: meal.slot,
      day_number: n,
      plan_slug: slug,
    });
    // The Protein Engine line travels with the dish, so a chosen sabzi shows its own guidance.
    const protein = meal.protein_suggestion;
    setRecipe({ title: meal.title, summary: meal.description, image: meal.image, protein_suggestion: protein });
    try {
      const { data } = await api.get(`/programs/${slug}/recipes/${meal.recipe_key}`);
      setRecipe({
        title: data.recipe?.title ?? meal.title,
        summary: data.recipe?.summary ?? meal.description,
        image: data.recipe?.image ?? meal.image,
        servings: data.recipe?.servings,
        prep_time: data.recipe?.prep_time,
        cook_time: data.recipe?.cook_time,
        ingredients: data.recipe?.ingredients ?? [],
        steps: data.recipe?.steps ?? [],
        nutrition: data.recipe?.nutrition ?? {},
        notes: data.recipe?.notes ?? [],
        protein_suggestion: protein,
        pending: !data.recipe,
      });
    } catch {
      setRecipe({
        title: meal.title,
        summary: meal.description,
        image: meal.image,
        protein_suggestion: protein,
        pending: true,
      });
    } finally {
      setLoadingRecipe(false);
    }
  };

  return (
    <AppShell
      title={`Day ${n}`}
      subtitle={payload?.program_title}
      slug={slug}
      todayHref={routes.day(slug, n)}
      size="reading"
      crumbs={[
        { label: "My Plans", to: routes.library },
        { label: payload?.program_title ?? "Program", to: routes.program(slug) },
        { label: "Day Index", to: routes.days(slug) },
        { label: `Day ${n}` },
      ]}
    >
      <div data-testid={PAGES.day} className="contents">
        {/* A light day header: where I am, how today is going. No dark hero. */}
        <header data-testid={KIT.dayHero} className="space-y-3 pb-1 pt-1">
          <p className="text-[11.5px] font-bold uppercase tracking-[0.1em] text-brand-orange">
            {payload?.program_title}
          </p>
          <h1 className="font-display text-3xl font-semibold leading-tight text-brand-teal sm:text-4xl">
            Day <span className="text-brand-orange">{n}</span>
          </h1>
          <div className="h-[5px] w-full overflow-hidden rounded-full bg-[#EEE9DE]">
            <div
              className="h-full rounded-full transition-[width] duration-700"
              style={{
                width: `${requiredSlots.length ? (eaten.filter((s) => requiredSlots.includes(s)).length / requiredSlots.length) * 100 : 0}%`,
                backgroundColor: "#DD4115",
              }}
            />
          </div>
          <p
            data-testid={KIT.dayHeroMeals}
            className="text-[14px] leading-[1.6] text-muted-foreground"
          >
            {dayNote ? `${dayNote} ` : ""}
            {eaten.filter((s) => requiredSlots.includes(s)).length} of {requiredSlots.length} meals
            eaten.
            {day?.protein_total?.grams > 0 && (
              <span data-testid="day-protein-total">
                {" "}
                About {day.protein_total.grams} g protein
                {day.protein_total.in_meals ? " in meals" : " per serving"}.
              </span>
            )}
          </p>
        </header>
        {planAhead && (
          <aside
            data-testid="plan-ahead-note"
            className="flex items-start gap-3.5 rounded-[20px] bg-brand-peach p-5"
          >
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-orange">
                Plan ahead
              </p>
              <p className="mt-1.5 text-[14px] leading-[1.6] text-brand-teal">{planAhead}</p>
            </div>
          </aside>
        )}
        {allRequiredDone && (
          <span
            data-testid="day-complete-badge"
            className="ps-pop -mt-2 inline-flex items-center gap-1.5 self-start rounded-full bg-brand-green/10 px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-[0.12em] text-brand-green"
          >
            <Check className="h-3.5 w-3.5" strokeWidth={3} /> Day complete
          </span>
        )}

        {isBundle
          ? day?.grocery_reminder && (
              <BundleNote
                kind="grocery"
                text={day.grocery_reminder}
                actionLabel="Open the Grocery Guide"
                onAction={() => navigate(routes.guide(slug, "grocery", n))}
              />
            )
          : day?.grocery_locked && (
              /* Grocery Engine is Bundle-only: one contextual prompt for the day, not per meal. */
              <BundleNote
                kind="grocery"
                locked
                upgradeUrl={payload?.native_upgrade_url || payload?.upgrade_url}
                upgradeLabel={payload?.native_upgrade_url ? "Upgrade to Complete Plan" : undefined}
              />
            )}

        {meals.length ? (
          <section className="space-y-3.5">
            <header className="flex items-baseline justify-between gap-3 px-0.5">
              <h2 className="ps-display text-[19px] font-bold sm:text-[21px]">Today's meals</h2>
              <span className="whitespace-nowrap text-[12.5px] font-medium text-brand-teal/45">
                Tick each one as you eat it
              </span>
            </header>

            {meals.map((meal) => (
              <article
                key={meal.slot}
                className={cn(
                  "rounded-[22px] border p-4 transition-colors sm:p-5",
                  eaten.includes(meal.slot)
                    ? "border-brand-teal/15 bg-white"
                    : "border-brand-teal/10 bg-white shadow-[0_1px_2px_rgba(15,46,44,0.04),0_10px_24px_-14px_rgba(15,46,44,0.18)]"
                )}
              >
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <p className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-brand-orangeInk">
                    {SLOT_LABELS[meal.slot] ?? meal.slot}
                  </p>
                  {meal.portion && (
                    <span className="rounded-full bg-brand-tealTint px-2.5 py-1 text-[11px] font-bold text-brand-tealSoft">
                      {meal.portion}
                    </span>
                  )}
                  {meal.required === false && (
                    <span className="rounded-full bg-brand-tealTint px-2.5 py-1 text-[11px] font-bold text-brand-tealSoft">
                      Optional
                    </span>
                  )}
                </div>

                <MealTile
                  meal={meal}
                  completed={eaten.includes(meal.slot)}
                  onToggle={toggle}
                  onOpenRecipe={openRecipe}
                />

                {isBundle && meal.protein_suggestion && (
                  <div className="mt-3.5">
                    <BundleNote kind="protein" label={payload?.protein_label} text={meal.protein_suggestion} />
                  </div>
                )}
                {/* Basic tier: the protein teaser appears once, against the first meal that has
                    guidance behind it — contextual, not repeated after every meal. */}
                {!isBundle && meal.protein_locked && meal.slot === firstProteinSlot && (
                  <div className="mt-3.5">
                    <BundleNote
                      kind="protein"
                      locked
                      label={payload?.protein_label}
                      upgradeUrl={payload?.native_upgrade_url || payload?.upgrade_url}
                      upgradeLabel={payload?.native_upgrade_url ? "Upgrade to Complete Plan" : undefined}
                    />
                  </div>
                )}
              </article>
            ))}
          </section>
        ) : (
          <EmptyState
            icon={UtensilsCrossed}
            title={payload === false ? "Day not found" : "Loading today's meals"}
            description={
              payload === false ? "This day is not part of the plan." : "Fetching the plan for this day."
            }
          />
        )}

        {/* Healthy tips are part of the plan itself — every reader gets them, no upsell. */}
        {day?.healthy_tip && <BundleNote kind="tip" text={day.healthy_tip} />}

        {/* One obvious next step, and it is never a Save button. */}
        <div className="space-y-3 pb-2 pt-1">
          <button
            data-testid="next-day-cta"
            disabled={!meals.length}
            onClick={() => navigate(onwardHref)}
            className={cn(
              "ps-tap group flex w-full items-center justify-between gap-4 rounded-[22px] px-6 py-5 text-left text-white transition-opacity duration-300 sm:px-7",
              meals.length ? "opacity-100 shadow-card" : "cursor-not-allowed opacity-55"
            )}
            style={{ backgroundColor: "#17403B" }}
          >
            <span>
              <span className="block font-display text-[17px] font-bold text-white sm:text-[19px]">
                {isLastDay
                  ? "Finish the plan"
                  : closesAWeek
                    ? `Week ${n / 7} complete`
                    : `Continue to Day ${n + 1}`}
              </span>
              <span className="mt-1 block text-[12.5px] font-semibold text-white/85">
                {allRequiredDone
                  ? "This day is done — your progress is saved"
                  : `${eaten.length} of ${requiredSlots.length} meals ticked so far`}
              </span>
            </span>
            <span className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-full bg-white/20">
              <ArrowRight className="h-[17px] w-[17px] text-white transition-transform duration-300 group-hover:translate-x-0.5" />
            </span>
          </button>
          {!!meals.length && !allRequiredDone && (
            <p className="px-3 text-center text-[12.5px] leading-relaxed text-muted-foreground">
              Move on whenever you like. Ticking meals is only for your own progress, and it saves on
              its own.
            </p>
          )}
        </div>
        {/* "Continue to Day X" above is the one forward action. Plan Overview and the Day Index live
            in the persistent bottom navigation, so they are not repeated here. */}
      </div>

      <RecipeModal
        open={!!recipe}
        loading={loadingRecipe}
        onOpenChange={(open) => !open && setRecipe(null)}
        recipe={recipe}
        proteinLabel={payload?.protein_label}
      />
    </AppShell>
  );
}
